import React, { useMemo } from "react";
import { Box, chakra, Flex } from "@chakra-ui/react";
import { getMarketingPageHeading } from "../../marketing-content/marketingContentLoader";
import { useLandingTheme } from "../landing-page/LandingThemeContext";
import { ThemedBody, ThemedContainer, ThemedDisplayTitle } from "../landing-page/sections/themed/ThemedLayout";
import { ThemedClosingCta } from "../landing-page/sections/themed/ThemedClosingCta";
import { UploadStacked } from "../landing-page/sections/hero/UploadStacked";
import { HeroAppStoreCallToAction, WEB_APP_CTA_CLASS_NAME } from "../landing-page/sections/AppStoreCallToAction";
import { LectureSlideToJungleQuestion } from "../landing-page/sections/hero/LectureSlideToJungleQuestion";
import TestimonialsSection from "../landing-page/sections/TestimonialsSection";
import LearnByTalkingSection from "../landing-page/sections/LearnByTalkingSection";
import ExamStyleSection from "../landing-page/sections/ExamStyleSection";
import GrowTreesSection from "../landing-page/sections/GrowTreesSection";
import { ChartQualitySection } from "../landing-page/sections/themed/ChartQualitySection";
import { MarketingPageMain } from "./MarketingSiteShell";
import { MarketingMarkdown } from "./MarketingMarkdown";
import { getFrontMatterString, MarketingTemplatePageProps } from "./marketingTemplatePageProps";
import { splitMarkdownBeforeHeading, splitRenderedHtmlBeforeHeading } from "./splitMarketingBody";

/**
 * The `landing` template (use-cases, conversions, alternatives): a landing-style hero with the
 * page's headline, subline and the same upload / paste-a-link control as the home page, the
 * `heroVideo` or the slide-to-question visual beside it (the language choice is in the footer, as on the home page), the shared testimonials, then the
 * Markdown body (how-to steps, comparison table, FAQ) in the reading column and the closing call
 * to action. The hero's h1 and subline carry the shared markup classes.
 *
 * A page with `productSectionsBeforeHeading` in its front matter (the Quizlet comparison) also shows the
 * home page's product sections, so the comparison is backed by the product itself: the question-quality
 * chart (whose competitors include the one being compared) in place of the testimonials, the voice tutor,
 * exam styles and progress sections before the body's heading of that number, and the testimonials
 * after the body.
 */
export const MarketingLandingTemplatePage = React.memo(function MarketingLandingTemplatePage({
  entry,
  frontMatter,
  markdownBody,
  renderedBodyHtml,
}: MarketingTemplatePageProps) {
  const theme = useLandingTheme();
  const heading = getMarketingPageHeading(entry);
  const subline = getFrontMatterString(frontMatter, "subline");
  const heroVideoUrl = getFrontMatterString(frontMatter, "heroVideo");
  const productSectionsBeforeHeading = frontMatter.productSectionsBeforeHeading;
  const showsProductSections = typeof productSectionsBeforeHeading === "number";
  // The comparison table names its columns from CSS on a phone (marketingContent.css); a product name is
  // the same in every language. JSON.stringify makes it a quoted CSS string.
  const comparedProductName = getFrontMatterString(frontMatter, "comparedProduct");
  const comparisonArticleStyle = useMemo(
    () => (comparedProductName ? ({ "--compared-product-name": JSON.stringify(comparedProductName) } as React.CSSProperties) : undefined),
    [comparedProductName]
  );
  const [markdownBeforeProductSections, markdownAfterProductSections] = showsProductSections
    ? splitMarkdownBeforeHeading(markdownBody, productSectionsBeforeHeading)
    : [markdownBody, ""];
  const [renderedHtmlBeforeProductSections, renderedHtmlAfterProductSections] =
    showsProductSections && renderedBodyHtml
      ? splitRenderedHtmlBeforeHeading(renderedBodyHtml, productSectionsBeforeHeading)
      : [renderedBodyHtml, null];

  return (
    <>
      <MarketingPageMain template="landing">
        <Box as="section" pt={{ base: "24px", lg: "56px" }} pb={{ base: "8px", lg: "24px" }}>
          <ThemedContainer>
            <Flex
              direction={{ base: "column", lg: "row" }}
              align={{ base: "stretch", lg: "center" }}
              gap={{ base: "48px", lg: "92px" }}
            >
              <Box flex="1" w="100%">
                <ThemedDisplayTitle
                  level="hero"
                  className="marketing-article-heading"
                  mb={subline ? "20px" : { base: "32px", lg: "44px" }}
                >
                  {heading}
                </ThemedDisplayTitle>
                {subline && (
                  <ThemedBody
                    className="marketing-article-subline"
                    fontSize={{ base: "18px", md: "20px" }}
                    maxW="560px"
                    mb={{ base: "32px", lg: "40px" }}
                  >
                    {subline}
                  </ThemedBody>
                )}
                {/* Phone visitors from a Meta ad see the store link instead (hidden, not removed, for hydration). */}
                <UploadStacked className={WEB_APP_CTA_CLASS_NAME} maxW="520px" />
                <HeroAppStoreCallToAction maxW="520px" />
              </Box>

              <Box flex="1" w="100%" maxW={{ base: "560px", lg: "none" }} position="relative">
                {heroVideoUrl ? (
                  <chakra.video
                    src={heroVideoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    w="100%"
                    display="block"
                    borderRadius={theme.shape.cardRadius}
                    boxShadow={theme.card.shadow}
                    bg={theme.color.surface}
                  />
                ) : (
                  <LectureSlideToJungleQuestion onLight />
                )}
              </Box>
            </Flex>
          </ThemedContainer>
        </Box>

        {showsProductSections ? <ChartQualitySection /> : <TestimonialsSection />}

        <article
          className={showsProductSections ? "marketing-article marketing-article--comparison" : "marketing-article"}
          style={comparisonArticleStyle}
        >
          <MarketingMarkdown
            className="marketing-article-body"
            markdown={markdownBeforeProductSections}
            renderedHtml={renderedHtmlBeforeProductSections}
          />
        </article>

        {showsProductSections && (
          <>
            <LearnByTalkingSection />
            <ExamStyleSection />
            <GrowTreesSection />
            {(markdownAfterProductSections || renderedHtmlAfterProductSections) && (
              <article className="marketing-article">
                <MarketingMarkdown
                  className="marketing-article-body"
                  markdown={markdownAfterProductSections}
                  renderedHtml={renderedHtmlAfterProductSections}
                />
              </article>
            )}
            <TestimonialsSection />
          </>
        )}
      </MarketingPageMain>
      <ThemedClosingCta />
    </>
  );
});
